import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/virtualizedTable";
import { createColumnHelper } from "@tanstack/react-table";
import { Users } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { LoadingIndicator } from "../components/LoadingIndicator";

interface EmployeeSummaryTableProps {
  summaries: Array<{
    employee_id: string;
    employee_name: string;
    month_year: string;
    allocated: number;
    granted: number;
    disbursed: number;
    count: number;
  }>;
  isLoading: boolean;
}

export function EmployeeSummaryTable({ summaries, isLoading }: EmployeeSummaryTableProps) {
  const columnHelper = createColumnHelper<typeof summaries[0]>();

  const columns = [
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: ({ table }: any) => (
        <>
          <button
            onClick={table.getToggleAllRowsExpandedHandler()}
            className="mr-2"
          >
            {table.getIsAllRowsExpanded() ? '▼' : '▶'}
          </button>
          Employee
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            paddingLeft: `${row.depth * 2}rem`,
          }}
          className="flex items-center gap-2"
        >
          {row.getCanExpand() ? (
            <button
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: 'pointer' }}
            >
              {row.getIsExpanded() ? '▼' : '▶'}
            </button>
          ) : null}
          {row.depth === 0 ? (
            <>
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{getValue()}</span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">{row.original.month_year}</span>
          )}
        </div>
      ),
      enableGrouping: true,
      aggregationFn: 'unique',
    }),
    columnHelper.accessor("count", {
      id: "count",
      header: "Total Requests",
      cell: (info) => <Badge variant="outline">{info.getValue()}</Badge>,
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }: any) => <Badge variant="outline">{getValue()}</Badge>,
    }),
    columnHelper.accessor("allocated", {
      id: "allocated",
      header: "Allocated",
      cell: (info) => <div className="font-medium">{formatCurrency(info.getValue())}</div>,
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }: any) => <div className="font-medium">{formatCurrency(getValue())}</div>,
    }),
    columnHelper.accessor("granted", {
      id: "granted",
      header: "Granted",
      cell: (info) => <div className="font-medium text-green-600">{formatCurrency(info.getValue())}</div>,
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }: any) => <div className="font-medium text-green-600">{formatCurrency(getValue())}</div>,
    }),
    columnHelper.accessor("disbursed", {
      id: "disbursed",
      header: "Disbursed",
      cell: (info) => <div className="font-medium text-blue-600">{formatCurrency(info.getValue())}</div>,
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }: any) => <div className="font-medium text-blue-600">{formatCurrency(getValue())}</div>,
    }),
    columnHelper.display({
      id: "grant_rate",
      header: "Grant Rate",
      cell: (info) => {
        const rate = info.row.original.allocated > 0 
          ? (info.row.original.granted / info.row.original.allocated) * 100 
          : 0;
        return (
          <Badge variant="secondary" className="text-xs">
            {rate.toFixed(0)}%
          </Badge>
        );
      },
      aggregatedCell: ({ row }: any) => {
        const totalAllocated = row.getValue('allocated');
        const totalGranted = row.getValue('granted');
        const rate = totalAllocated > 0 ? (totalGranted / totalAllocated) * 100 : 0;
        return (
          <Badge variant="secondary" className="text-xs">
            {rate.toFixed(0)}%
          </Badge>
        );
      },
    }),
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Employee Summary <LoadingIndicator isLoading={isLoading} /></CardTitle>
        <CardDescription>
          <p>Employee requests grouped by Month + Year for selected years, versus the amount allocated for that year (not rolling)</p>
        </CardDescription>

      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <DataTable
          defaultSorting={[{ id: "employee_name", desc: false }]}
          defaultGrouping={["employee_name"]}
          columns={columns as any[]}
          data={summaries}
          height="calc(100vh - 380px)"
          enableColumnResizing={false}
          emptyResultsMessage={isLoading ? "Loading rows..." : "No results."}
        />
      </CardContent>
    </Card>
  );
}